import React, { forwardRef } from "react";
import {
  buttonStyles,
  type ButtonSize,
  type ButtonVariant,
} from "./buttonStyles";

export type { ButtonSize, ButtonVariant } from "./buttonStyles";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  fullWidth?: boolean;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
}

const LoadingSpinner = () => (
  <svg
    className="h-4 w-4 animate-spin"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="9"
      stroke="currentColor"
      strokeWidth="3"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M21 12a9 9 0 0 0-9-9v3a6 6 0 0 1 6 6h3Z"
    />
  </svg>
);

const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "secondary",
    size = "sm",
    loading = false,
    fullWidth = false,
    leadingIcon,
    trailingIcon,
    disabled = false,
    type = "button",
    className = "",
    children,
    ...props
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={buttonStyles({ variant, size, fullWidth, className })}
      disabled={loading || disabled}
      aria-busy={loading || undefined}
      {...props}
    >
      <span
        className={`inline-flex items-center justify-center gap-2 ${loading ? "invisible" : ""}`}
      >
        {leadingIcon}
        {children}
        {trailingIcon}
      </span>
      {loading ? (
        <span className="absolute inset-0 flex items-center justify-center">
          <LoadingSpinner />
          <span className="sr-only">Loading</span>
        </span>
      ) : null}
    </button>
  );
});

export default Button;
